import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  AppConfigService,
  CookieStorageService,
  TypedDictionary,
  WindowService,
} from '@core/index';
import { CityPairSelectorComponent } from '@dynamic-components/booker-v2/shared/city-pair-selector';
import { areAnyAirportsSeasonalRoutes } from '@dynamic-components/booker-v2/shared/city-pair-selector/city-pair-selector.utils';
import { DateRangePickerComponent } from '@dynamic-components/booker-v2/shared/date-range-picker/date-range-picker.component';
import { PromoCodeV2Component } from '@dynamic-components/booker-v2/shared/promo-code-v2';
import { PromoCodeResponse } from '@dynamic-components/booker-v2/shared/promo-code-v2/promo-code.type';
import { TravelerSelectorComponent } from '@dynamic-components/booker-v2/shared/traveler-selector';
import { BOOKER_CONFIG } from '@shared/booker';
import { BookerResponse, BookerType } from '@shared/booker/types';
import { BOOKER_TRIP_TYPES } from '@shared/booker/types/booker-trip-type.enum';
import {
  initTripTypesV2,
  isMultiCity,
  isRoundTrip,
  prePopTravelerSelector,
  prePopTravelersSelector,
} from '@shared/booker/utils/booker-utils';
import { CheckBox, RecentAirSearch } from '@shared/types';
import { getControlVal } from '@shared/ui/utils/form-control-utils/get-control-val';
import { booleanStringCheck } from '@shared/ui/utils/global-utils/boolean-string-check';
import { generateUniqueId } from '@shared/ui/utils/global-utils/generate-unique-id';
import { BookerFacade } from '@store/booker/booker.facade';
import { PairedRecentAirportSearches } from '@store/booker/types/booker-selector.types';
import { TripType } from '@store/cart/types/cart.enum';
import { Airport, CountryWithAirports, Region } from '@store/origins/types';
import { startOfToday } from 'date-fns';
import {
  IntersectionStatus,
  JbMonthVisibilityInterface,
} from 'jb-component-library';
import { ObservableInput } from 'ngx-observable-input';
import { BehaviorSubject, combineLatest, Observable, zip } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  skip,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import { BookerMultiCityStore } from '../booker-air-multi-city/store/booker-multi-city.store';
import { BookerAirFormService } from '../service/booker-air-form.service';
import { BookerAirStore } from '../store/booker-air.store';
import {
  enableAllDates,
  setOverlayMessageForMonth,
} from '../utils/schedule.utils';

@Component({
  selector: 'dot-booker-air-form',
  styleUrls: [
    '../../../../../shared/booker/booker.component.scss',
    './booker-air-form.component.scss',
  ],
  templateUrl: './booker-air-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookerAirFormComponent implements OnInit, AfterViewInit {
  private _destroy = inject(DestroyRef);

  @Input() bookerId: string;
  @Input() form: UntypedFormGroup;
  @Input() content: BookerResponse;
  @Input() isInterlineRoute = false;
  @Input() isInternationalRoute = false;
  @Input() showCaptcha = false;
  @Input() isMobile = false;
  @Input() regions: TypedDictionary<Region>;
  @Input() origins: CountryWithAirports[] = [];
  @Input() destinations: CountryWithAirports[] = [];
  @Input() recentAirports: PairedRecentAirportSearches[] = [];
  @Input() tripType: BOOKER_TRIP_TYPES = BOOKER_TRIP_TYPES.ROUNDTRIP;
  @Input() showTrueBlue = true;
  @Input() isTrueBlue: boolean;
  @Input() arrivalAirport: string;
  @Input() arrivalAirportError: string;
  @Input() departureAirport: string;
  @Input() departureAirportError: string;
  @Input() departDate: null | Date;
  @Input() departureDateError: string;
  @Input() returnDate: null | Date;
  @Input() returnDateError: string;
  @Input() promoCodeWithSource: {
    promoCode: string;
    source: 'url' | 'recent' | 'input';
  };
  @Input() origin: Airport;
  @Input() originError: string;
  @Input() set destination(value: Airport) {
    if (!value || value === undefined) return;
    this._destination.next(value);
  }
  get destination() {
    return this._destination.getValue();
  }
  @Input() destinationError: string;
  @Input() travelerCountError: string;
  @Input() showFormErrors = false;

  @ObservableInput() @Input('airportHashMap') airportHashMap$: Observable<
    TypedDictionary<Airport>
  >;

  @Input() showValidationErrors = false;
  // observes when travelers count changes in booker
  @Input() set recentTravellerCount(recentTravelers: RecentAirSearch) {
    prePopTravelersSelector(recentTravelers, this.form.get('travelers'));
  }

  @ViewChild('captchaRef') captchaRef: ElementRef;
  @ViewChild(DateRangePickerComponent, { static: false })
  dateRangePickerComponent: DateRangePickerComponent;

  @ViewChild(CityPairSelectorComponent)
  cityPairSelectorComponent: CityPairSelectorComponent;

  @ViewChild('travelerSelector')
  travelerSelectorComponent: TravelerSelectorComponent;

  @ViewChild(PromoCodeV2Component)
  promoCodeV2Component: PromoCodeV2Component;

  @Output() onCityPairChange = new EventEmitter<{
    origin: Airport;
    destination: Airport;
  }>();
  @Output() onOriginChange = new EventEmitter<Airport>();
  @Output() onDestinationChange = new EventEmitter<Airport>();

  private _destination = new BehaviorSubject<Airport>(null);
  isCalendarOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  bookerConfig = BOOKER_CONFIG;
  promoCodeData: PromoCodeResponse;
  hasPromoCode: boolean;

  originId: string;
  destinationId: string;
  departId: string;
  returnId: string;
  travelerSelectorId: string;
  nativeElem;
  bookWithPoints: CheckBox;

  fromAirport: AbstractControl;
  toAirport: AbstractControl;

  showShortCityName: boolean;
  keepCalendarOpen: boolean;
  isRoundTrip: boolean;
  isMultiCity: boolean;
  isSeasonalRoute = false;
  separatePartnerCities: boolean;
  tripTypeEnum = TripType;
  tripTypes;

  todayDate: Date = startOfToday();
  bookerType = BookerType;
  showCalendarRoundtripPrices: boolean;

  /**
   * Months that have been viewed/requested. Not months that have been requested to bff and cached.
   * Roundtrip pricing can view a month, but request from bff more than the viewed month. The is
   * to keep track of months that have just been viewed.
   */
  requestedMonths = new Set<number>();

  totalCost$ = this.bookerAirStore.totalCost$;

  get trueblueLabel() {
    return this.content.bookMessages.bookWithPoints;
  }

  get isScheduleAndPricingDisabled() {
    return (
      !booleanStringCheck(this.content.showSchedule) &&
      !booleanStringCheck(this.content.showPricingInCalendar)
    );
  }

  readonly vm$ = this.bookerAirStore.vm$;

  constructor(
    public windowService: WindowService,
    public element: ElementRef,
    public bookerFacade: BookerFacade,
    public route: ActivatedRoute,
    public cookieStorageService: CookieStorageService,
    private appConfig: AppConfigService,
    private readonly bookerMultiCityStore: BookerMultiCityStore,
    private readonly bookerAirStore: BookerAirStore,
    private readonly bookerAirFormService: BookerAirFormService,
  ) {
    // to-do: investigate updating auto-progress strategy
    this.originId = generateUniqueId('city-selector');
    this.destinationId = generateUniqueId('city-selector');
    this.departId = generateUniqueId('departure-date');
    this.returnId = generateUniqueId('return-date');
    this.travelerSelectorId = generateUniqueId('traveler-selector');
    this.nativeElem = this.element.nativeElement;
  }

  public focusOnDepartureField(options?) {
    this.dateRangePickerComponent.focusOnDepartureField(options);
  }

  public focusOnReturnField(options?) {
    this.dateRangePickerComponent.focusOnReturnField(options);
  }

  public focusOnPassengersField() {
    this.travelerSelectorComponent.focusOnTravellerSelector();
  }

  public focusOnOriginAirportField() {
    this.cityPairSelectorComponent?.focusOnOriginInput();
  }

  public focusOnDestinationAirportField() {
    this.cityPairSelectorComponent?.focusOnDestinationInput();
  }

  public focusOnCaptchaField() {
    this.captchaRef.nativeElement.focus();
  }

  ngOnInit(): void {
    combineLatest([
      this.isCalendarOpen$,
      this.form.controls['tripType'].valueChanges.pipe(distinctUntilChanged()),
    ])
      .pipe(takeUntilDestroyed(this._destroy), delay(25), skip(1))
      .subscribe(([isCalendarOpen, _]) => {
        this.setupMonthData();
        if (
          this.isRoundTrip &&
          !this.isMultiCity &&
          this.dateRangePickerComponent.endDateControl.value &&
          !isCalendarOpen
        ) {
          this.hasPromoCode
            ? this.promoCodeV2Component?.focusOnInput()
            : this.focusOnSubmit();
        }
      });

    if (
      this.content?.dateAggregator?.roundtripPricing &&
      'showCalendarRoundtripPrices' in
        this.content?.dateAggregator?.roundtripPricing
    ) {
      this.showCalendarRoundtripPrices = booleanStringCheck(
        this.content.dateAggregator.roundtripPricing
          .showCalendarRoundtripPrices,
      );
    } else {
      this.showCalendarRoundtripPrices =
        this.appConfig.roundTrip.showCalendarRoundtripPrices;
    }

    this.tripTypes = initTripTypesV2(
      this.content.bookMessages.roundTrip,
      this.content.bookMessages.oneWay,
      this.content.bookMessages.multicity.title,
      this.bookerConfig.SEARCH_TYPES,
      this.tripType,
    );

    this.showShortCityName = this.content.showShortCityName === 'true';
    this.keepCalendarOpen = this.content.keepCalendarOpen === 'true';
    this.separatePartnerCities = booleanStringCheck(
      this.content.separatePartnerCities,
    );
    this.bookerFacade.bookerGlobalSettings$
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe(settings => {
        this.hasPromoCode =
          this.content.showAirPromoCode === 'true' ||
          this.promoCodeWithSource.source === 'url' ||
          settings.showPromoCode === 'true';
      });

    this.promoCodeData = this.content.promoCode;

    this.form
      .get(['flights', '0'])
      .valueChanges.pipe(takeUntilDestroyed(this._destroy))
      .subscribe(({ origin, destination }) => {
        this.isSeasonalRoute = areAnyAirportsSeasonalRoutes(
          origin,
          destination,
        );
      });

    getControlVal(this.form.get('tripType'))
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe((value: string) => {
        this.isRoundTrip = isRoundTrip(value);
        this.isMultiCity = isMultiCity(value);
        this.form.get('tripType').setValue(value);
        // DOT-10299 clear return date if before depart date
        // and focus on the return date input
        if (
          this.departDate &&
          this.returnDate &&
          this.departDate > this.returnDate
        ) {
          this.returnDate = null;
          setTimeout(() => this.focusOnReturnField(), 5);
        }
      });
  }

  ngAfterViewInit(): void {
    this._destination
      .pipe(
        takeUntilDestroyed(this._destroy),
        filter(val => !!val),
        take(1),
        withLatestFrom(this.bookerFacade.openDatePickerInUrl$),
        delay(0), // equivalent to setTimeout(0) to ensure pricing shows upon opening calendar
      )
      .subscribe(([_, openDataPicker]) => {
        if (openDataPicker) {
          this.dateRangePickerComponent.openCalendar();
        }
      });
  }

  handleVisibilityChange(monthVisibilityObj: JbMonthVisibilityInterface): void {
    if (monthVisibilityObj.visibility === IntersectionStatus.Visible) {
      this.requestMonthData(monthVisibilityObj.month);
    }
  }

  resolved(captchaResponse: string) {
    console.info(`Resolved captcha with response ${captchaResponse}:`);
    this.cookieStorageService.setCaptchaTokenCookie(captchaResponse);
    this.form.patchValue({ recaptchaReactive: true });
  }

  isCaptchInvalid(): boolean {
    return (
      this.form.get('recaptchaReactive').invalid &&
      !this.form.get('recaptchaReactive').touched
    );
  }

  getRecentSearches(): Observable<RecentAirSearch[]> {
    return this.bookerFacade.recentAirSearches;
  }

  // this only fires for multicity right now
  // todo: setup multicity to use same system as other components for initial state from local storage
  setPrePopulatedSearch(
    recentAirSearches: RecentAirSearch[],
    airportHashMap = {},
  ) {
    if (!!recentAirSearches && !!recentAirSearches.length) {
      this.form.patchValue({ tripType: BOOKER_TRIP_TYPES.MULTICITY });
      prePopTravelerSelector(recentAirSearches[0], this.form.get('travelers'));
      this.bookerMultiCityStore.setPrePopulatedSearch(
        recentAirSearches,
        airportHashMap,
      );
    }
  }

  focusOnSubmit() {
    this.nativeElem.getElementsByClassName('jb-booker-air-submit')[0]?.focus();
  }

  onStartDateChange(startDate: string): void {
    // if its one way and single city trip
    if (startDate === null) return;
    if (!this.isRoundTrip && !this.isMultiCity && startDate) {
      this.focusOnSubmit();
    }
  }

  onEndDateChange(endDate: string): void {
    // if its round trip and single city trip
    if (
      this.isRoundTrip &&
      !this.isMultiCity &&
      !this.hasPromoCode &&
      endDate
    ) {
      this.focusOnSubmit();
    }
  }

  onPromoCodeChange(): void {
    this.focusOnSubmit();
  }

  clearDatePickerMonthValues() {
    this.dateRangePickerComponent?.resetAvailability();
  }

  onFlyoutStateChange(isCalendarOpen: boolean) {
    this.isCalendarOpen$.next(isCalendarOpen);
  }

  private setupMonthData() {
    this.clearDatePickerMonthValues();
    this.bookerAirStore.clearMonthPricingCache();
    this.requestedMonths.clear();
    if (this.isCalendarOpen$.value) {
      // calendar opened
      // load the first two months from the current date or the selected departure/return date.
      const firstMonth = this.departDate
        ? new Date(this.departDate)
        : this.returnDate
        ? new Date(this.returnDate)
        : new Date();
      // cannot use same date object because the value will change in the middle of the request
      // so make a copy of the first then add one to the month
      const secondMonth = new Date(firstMonth);
      // set to first day of month cause jan 31 + 1 month would be Feb 31 which doesnt exists
      // which will resolve to march 3, so instead jan 1 + 1 month is feb 1
      secondMonth.setDate(1);
      secondMonth.setMonth(secondMonth.getMonth() + 1); // add one month to request the next one

      this.requestMonthData(firstMonth);
      this.requestMonthData(secondMonth);
    }
  }

  /** Request data for the month, determine if to use single or roundtrip request */
  private requestMonthData(month) {
    if (this.requestedMonths.has(month.getMonth())) {
      // if already requested month, dont
      return;
    }
    if (this.showCalendarRoundtripPrices && this.isRoundTrip) {
      this.requestRoundTrip(month);
    } else {
      this.requestBFFService(month);
    }
    this.requestedMonths.add(month.getMonth());
  }

  private requestBFFService(month: Date): void {
    const flights = this.form.get(['flights', '0']).value;
    if (!flights?.origin?.code || !flights?.destination?.code) {
      enableAllDates(
        month.getMonth(),
        month.getFullYear(),
        this.dateRangePickerComponent,
      );
      // need both origin and destination to continue
      return;
    }

    zip(
      this.bookerAirStore
        .getCurrentMonthCache$(month.getFullYear(), month.getMonth() + 1)
        .pipe(take(1)),
      this.bookerAirStore.getAllMonthsCacheAsArray$.pipe(take(1)),
      this.bookerAirStore.departDate$.pipe(take(1)),
      this.bookerAirStore.returnDate$.pipe(take(1)),
    )
      .pipe(take(1))
      .subscribe(([curMonthCache, allMonthsCache, departDate, returnDate]) => {
        if (!curMonthCache) {
          // if data is not in the cache, set the loading overlay and make a call to the API
          if (this.appConfig.bookerCalendarRemoveLoadOverlay) {
            enableAllDates(
              month.getMonth(),
              month.getFullYear(),
              this.dateRangePickerComponent,
              this.appConfig.bookerCalendarRemoveLoadOverlayLoadingString,
            );
          } else {
            setOverlayMessageForMonth(
              this.dateRangePickerComponent,
              month.getFullYear(),
              month.getMonth(),
              this.content.dateAggregator.states.serviceLoading,
              this.content.dateAggregator.states.serviceLoading,
            );
          }

          this.bookerAirStore.getBffSingleResponse({
            month,
            datePickerRef: this.dateRangePickerComponent,
          });
        }

        const isDateInDateArray = date =>
          allMonthsCache.some(cacheItem => {
            return (
              cacheItem.month === String(date.getMonth() + 1) &&
              cacheItem.year === String(date.getFullYear())
            );
          });

        // if we dont have both origin and destination code bffSingleResponse will filter out the call, nothing happens
        if (departDate) {
          const isDepartDateInCache = isDateInDateArray(departDate);
          if (
            !isDepartDateInCache &&
            month.getMonth() !== departDate.getMonth()
          ) {
            this.bookerAirStore.getBffSingleResponse({
              month: departDate,
              datePickerRef: this.dateRangePickerComponent,
            });
          }
        }
        if (returnDate) {
          const isReturnDateInCache = isDateInDateArray(returnDate);
          if (
            !isReturnDateInCache &&
            month.getMonth() !== returnDate.getMonth()
          ) {
            this.bookerAirStore.getBffSingleResponse({
              month: returnDate,
              datePickerRef: this.dateRangePickerComponent,
            });
          }
        }
      });
  }

  /**
   * Request to get round trip data for a month and set it to the dateAggregator
   * @param month Month for the round trip data
   * @returns
   */
  private requestRoundTrip(month: Date): void {
    const flights = this.form.get(['flights', '0']).value;
    // enable all dates if there is no origin, no destination or schedule and pricing is off
    if (
      !flights?.origin?.code ||
      !flights?.destination?.code ||
      this.isScheduleAndPricingDisabled
    ) {
      enableAllDates(
        month.getMonth(),
        month.getFullYear(),
        this.dateRangePickerComponent,
      );
      // need both origin and destination to continue
      return;
    }

    if (this.appConfig.roundTrip.removeLoadOverlay) {
      // enable all dates with possible loading text
      enableAllDates(
        month.getMonth(),
        month.getFullYear(),
        this.dateRangePickerComponent,
        this.appConfig.roundTrip.removeLoadOverlayLoadingString,
      );
    } else {
      // set loading overlay
      setOverlayMessageForMonth(
        this.dateRangePickerComponent,
        month.getFullYear(),
        month.getMonth(),
        this.content.dateAggregator.states.serviceLoading,
        this.content.dateAggregator.states.serviceLoading,
      );
    }

    this.bookerAirFormService
      .requestRoundTrip(month)
      .pipe(take(1))
      .subscribe({
        next: res => {
          this.dateRangePickerComponent.setMonthData(res);
        },
        error: error => {
          // error throw from api failure
          if (error.message === 'apiFailure') {
            enableAllDates(
              month.getMonth(),
              month.getFullYear(),
              this.dateRangePickerComponent,
            );
          } else {
            throw error;
          }
        },
      });
  }

  async handleEndDateFocused() {
    this.bookerAirStore.updateFocusedInput('return');

    // first check round trip prices on, then must have start date then whether months data has been loaded
    if (
      this.showCalendarRoundtripPrices &&
      this.form.controls['departureDate'].value &&
      this.dateRangePickerComponent.hasLoadedMonths // prevents calls when the end date is click to open calendar cause calculations handled after api calls already
    ) {
      const recalculatedMonths =
        await this.bookerAirFormService.recalculateMonthsDataRelativeToDepartDate(
          this.form.controls['departureDate'].value,
          this.requestedMonths,
        );
      if (recalculatedMonths) {
        for (const month of recalculatedMonths) {
          this.dateRangePickerComponent.setMonthData(month);
        }
      }
    }
  }

  async handleStartDateFocused() {
    this.bookerAirStore.updateFocusedInput('depart');

    if (
      this.showCalendarRoundtripPrices &&
      this.form.controls['departureDate'].value &&
      this.dateRangePickerComponent.hasLoadedMonths && // prevents calls when the start date is click to open calendar cause calculations handled after api calls already
      this.keepCalendarOpen // only following in case when calender is kept open
    ) {
      // when calendaOpen is true the user can play around with the end dates and then come back to depart date
      // in those cases the prices on the depart calendar need to be recalculated to reflect the possibly changed
      // triplength. This is not a problem when its false because the calendar closes and opening it again recalculates everything
      const recalculatedMonths =
        await this.bookerAirFormService.recalculateMonthsDataRelativeToDepartDate(
          this.form.controls['departureDate'].value,
          this.requestedMonths,
        );
      if (recalculatedMonths) {
        for (const month of recalculatedMonths) {
          this.dateRangePickerComponent.setMonthData(month);
        }
      }
    }
  }
}
